import { useState, useMemo } from "react"
import dayjs from "dayjs"
import { useParams } from "react-router-dom"

// components
import { Form, Upload, Flex } from "antd"
import { PrimeButton, Select, TextInput, TimePicker, DatePicker } from "components-design-system"

// utils
import jsonToFormData from "utilities/jsonToFormData"
import { truncatedFileName } from "utilities/fileUtils"

// apis
import { usePostTimelineComment, usePutTimelineComment } from "api/hooks/studies/useTimelineFeedApi"
import { usePostActivityComment, usePutActivityComment } from "api/hooks/activities/useActivitiesApi"
import { usePostRequestFeedComment, usePutRequestFeedComment } from "api/hooks/requests/useRequestTimelineApi"
import { useGetTags } from "api/hooks/enum"

// styles
import "./FeedCommentEditForm.scss"

const FeedCommentEditForm = ({ type, timelineFeedParams, selectedComment, onCloseEditComment, refetchTimeline }) => {
  const { studyId, activityId, requestId } = useParams()
  const { data: tagOptions = [] } = useGetTags({ query: "" })
  const [formLoading, setFormLoading] = useState(false)

  // study apis
  const postStudyComment = usePostTimelineComment(studyId, timelineFeedParams)
  const putStudyComment = usePutTimelineComment(studyId, selectedComment?.id, timelineFeedParams)

  // activitiy apis
  const postActivityComment = usePostActivityComment(studyId, activityId, timelineFeedParams)
  const putActivityComment = usePutActivityComment(studyId, activityId, selectedComment?.id, timelineFeedParams)

  // request apis
  const postRequestComment = usePostRequestFeedComment(requestId, selectedComment?.id)
  const putRequestComment = usePutRequestFeedComment(requestId, selectedComment?.id)

  const [form] = Form.useForm()

  const maxSize = 3 * (1024 * 1024)

  const postComment = useMemo(() => {
    if (type === "study") return postStudyComment
    else if (type === "activity") return postActivityComment
    else if (type === "request") return postRequestComment
  }, [selectedComment, type])

  const putComment = useMemo(() => {
    if (type === "study") return putStudyComment
    else if (type === "activity") return putActivityComment
    else if (type === "request") return putRequestComment
  }, [selectedComment, type])

  const checkFileSize = (_, fileList) => {
    if (fileList.size && fileList.length > 0 && !selectedComment) {
      const file = fileList[0]
      const isFileSizeValid = file.size < maxSize
      if (!isFileSizeValid) {
        return Promise.reject(new Error("File size exceeds the 3MB limit. Please upload another file."))
      } else {
        return Promise.resolve()
      }
    }

    return Promise.resolve()
  }

  const onChange = (params) => {
    const file = params?.fileList[0]
    if (file?.name) {
      file.name = file && truncatedFileName(file?.name, 40)
    }
  }

  const selectedCommentFileName =
    selectedComment && truncatedFileName(selectedComment?.attachment?.split("/")?.pop(), 40)

  const onSaveChanges = (data) => {
    setFormLoading(true)
    const payload = { text: data.text, tags: data.tags }

    if (data.dragger && data.dragger.length > 0) {
      if (data?.dragger[0].originFileObj) {
        payload.attachment = data?.dragger[0].originFileObj
      }
    } else {
      payload.attachment = ""
      payload.attachment_url = ""
    }
    const dateAndTimeCombined = data.date + " " + data.time

    const formattedComment = jsonToFormData({
      ...payload,
      action_date: dayjs(dateAndTimeCombined).format(),
    })

    const headers = { "Content-Type": "multipart/form-data" }

    if (selectedComment?.id) {
      putComment(
        {
          body: formattedComment,
          headers,
        },
        {
          onSuccess: () => {
            refetchTimeline()
            form.resetFields()
            onCloseEditComment()
          },
          onSettled: () => {
            setFormLoading(false)
          },
        }
      )
    } else {
      postComment(
        {
          body: formattedComment,
          headers,
        },
        {
          onSuccess: () => {
            refetchTimeline()
            form.resetFields()
            onCloseEditComment()
          },
          onSettled: () => {
            setFormLoading(false)
          },
        }
      )
    }
  }

  return (
    <Form
      disabled={formLoading}
      layout="vertical"
      form={form}
      className="timeline-comment-form-container"
      onFinish={onSaveChanges}
      initialValues={{
        date: selectedComment ? dayjs(selectedComment.action_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
        time: selectedComment ? dayjs(selectedComment.action_date).format("h:mm:ss A") : dayjs().format("h:mm:ss A"),
        text: selectedComment
          ? selectedComment.project_id
            ? selectedComment.timeline_text.split(":").slice(1).join(":").trim()
            : selectedComment.timeline_text
          : "",
        tags: selectedComment ? selectedComment.tags || [] : [],
        dragger: [{ name: selectedComment?.attachment?.split("/")?.pop(), url: selectedComment?.attachment_url }],
      }}
    >
      <Flex
        vertical
        gap={12}
      >
        <div className="comment-form-date-container">
          <Form.Item
            label={"Date"}
            name={"date"}
            rules={[{ required: true }]}
          >
            <DatePicker allowClear={false} />
          </Form.Item>
          <Form.Item
            name={"time"}
            label={"Time"}
            layout="vertical"
            rules={[{ required: true }]}
          >
            <TimePicker
              use12Hours
              format="h:mm:ss A"
              allowClear={false}
            />
          </Form.Item>
        </div>
        <Form.Item
          label={"Comment"}
          name={"text"}
          rules={[{ required: true }]}
        >
          <TextInput
            rows={3}
            type={"textarea"}
            autoSize={{
              minRows: 3,
            }}
            placeholder={"Enter comment here"}
          />
        </Form.Item>

        <Form.Item
          name="tags"
          label={"Tags"}
        >
          <Select
            mode="tags"
            placeholder={"Type a label name here"}
            options={tagOptions}
          />
        </Form.Item>
      </Flex>
      <p className="comment-form-tags-label">
        Tags: Assigning labels allows for easier filtering and categorization of comments
      </p>

      <Form.Item
        name="dragger"
        getValueFromEvent={({ fileList }) => fileList}
        rules={[{ validator: checkFileSize }]}
      >
        <Upload.Dragger
          defaultFileList={
            selectedComment?.attachment && [{ name: selectedCommentFileName, url: selectedComment?.attachment_url }]
          }
          name="files"
          multiple={false}
          maxCount={1}
          action={"/"}
          beforeUpload={() => false}
          onChange={onChange}
        >
          <span className="ant-upload-drag-icon">
            <i
              className="fal fa-cloud-upload"
              style={{ fontSize: "22px", color: `var(--prime-color-text-tertiary-default)` }}
            />
          </span>
          <p
            className="ant-upload-text"
            style={{ color: `var(--prime-color-text-tertiary-default)`, fontSize: "12px" }}
          >
            Drag and drop files to upload or <span className="link">browse*</span>.
          </p>
        </Upload.Dragger>
      </Form.Item>

      <span className="comment-form-tags-label">Only one file can be attached. 3MB max file size.</span>

      <div className="comment-form-modal-actions">
        <PrimeButton
          onClick={() => {
            onCloseEditComment()
            form.resetFields()
          }}
        >
          Cancel
        </PrimeButton>
        <PrimeButton
          type="primary"
          htmlType="submit"
          loading={formLoading}
        >
          Save Changes
        </PrimeButton>
      </div>
    </Form>
  )
}

export default FeedCommentEditForm
